/**
 * RV9 · Flow is laid out, never drawn.
 *
 * Order is position (R-12). Nodes carry no stored coordinates and every edge comes from the
 * sequence, so a process that cannot execute cannot be authored — if an author could connect
 * 7.4 back to 7.1 by hand, the record would have no execution order.
 *
 * Pure geometry: no React, no DOM, no data access. That is what lets the shapes be reasoned
 * about and tested without a browser.
 *
 * Differs from the reference in one way, and it is D2's ruling: a loop's body is its
 * **children**, not a range of siblings. So the region is measured around a subtree rather
 * than around `steps[a..b]`, and there is no `bodyRange` to get wrong.
 */
export const NW = 300;   // node width — three lines: what · kind · who/records
export const NH = 96;    // step height — a two-line title, the kind, who and one fact
export const DH = 44;    // decision height
const NW_DEFAULT = NW, NH_DEFAULT = NH, DH_DEFAULT = DH;
export const GY = 40;    // vertical gap
export const GX = 24;    // gap between decision lanes
export const PADX = 44;
export const PADY = 34;
/** 2026-10-13 · how far right of the nodes a decision's dependency edge runs. */
export const DEPX = 40;
/** 2026-10-22 · how far right of a node's box a step's own dependency edge runs, and its corner radius. */
export const AFTER_X = 18;
export const EDGE_R = 9;

/**
 * @param {object[]} children  the operation's top-level nodes, each with {code, kind, ...}
 * @returns {{nodes, edges, lanes, groups, width, height}}
 */
export function layout(children, opts = {}) {
  // 2026-10-15 · sizes and terminals are the caller's: the AVEVA-style flow draws smaller nodes
  // (an icon tile with its name under it) between a Start and an End. Defaults are unchanged.
  const NW = opts.nw ?? NW_DEFAULT;
  const NH = opts.nh ?? NH_DEFAULT;
  const DH = opts.dh ?? DH_DEFAULT;
  const TS = 44;   // a terminal's size
  const nodes = [];
  const edges = [];
  const lanes = [];
  const groups = [];
  const byCode = {};

  /**
   * Lay a run of siblings in a column centred on `cx`, starting at `y`.
   * Returns {y, last} so the caller can carry on below it.
   */
  function run(list, cx, y, entry) {
    let prev = entry;
    // 2026-10-22 (owner) · between two steps an edge is drawn only where one depends on the other
    // (step_dependency.py) — "if dependency is not there then edges should not get between two nodes".
    // Into and out of a decision, a repeat, Start and End the sequence edges stay.
    const items = list || [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.kind === "DECISION") {
        const dn = { kind: "dec", code: item.code, node: item, x: cx, y, w: NW, h: DH };
        nodes.push(dn);
        byCode[item.code] = dn;
        if (prev) edges.push({ a: prev, b: dn });
        y += DH + GY;

        const laneNodes = (item.children || []).filter((c) => c.kind === "LANE");
        const total = Math.max(1, laneNodes.length) * NW +
                      Math.max(0, laneNodes.length - 1) * GX;
        let lx = cx - total / 2 + NW / 2;
        let maxY = y;
        const ends = [];

        for (const lane of laneNodes) {
          lanes.push({ x: lx, y: y - 25, label: lane.label || lane.code, outcome: lane.outcome || null });
          const r = run(lane.children, lx, y, dn);
          ends.push(r.last || dn);
          maxY = Math.max(maxY, r.y);
          lx += NW + GX;
        }
        if (!laneNodes.length) ends.push(dn);

        y = maxY;
        const jn = { kind: "join", x: cx, y, w: 16, h: 16 };
        nodes.push(jn);
        ends.forEach((e) => edges.push({ a: e, b: jn }));
        prev = jn;
        y += 16 + GY;
        continue;
      }

      if (item.kind === "LOOP") {
        // The band is measured around the subtree, because the body IS the children (D2).
        const top = y;
        const head = { kind: "loop", code: item.code, node: item, x: cx, y, w: NW, h: DH };
        nodes.push(head);
        if (prev) edges.push({ a: prev, b: head });
        y += DH + GY;

        const r = run(item.children, cx, y, head);
        const inner = r.last || head;
        y = r.y;

        groups.push({
          code: item.code,
          label: `for each ${item.payload?.over || "—"}`
                 + (item.payload?.expected ? "" : item.payload?.kind === "UNTIL_RULE" && item.payload?.ref ? ` · until ${item.payload.ref}`
                    : item.payload?.ref ? ` · count from ${item.payload.ref} at issue` : ""),
          count: item.payload?.expected ?? null,
          x1: cx - NW / 2 - 22,
          x2: cx + NW / 2 + 22,
          y1: top - 26,
          y2: y - GY + 18,
        });
        // The back edge sits OUTSIDE the region: it is the repeat, not a step in it.
        edges.push({ a: inner, b: head, back: true });
        prev = inner;
        continue;
      }

      if (item.kind === "HEADING") continue;   // layout, not a step (see tree.py)

      const nn = { kind: "step", code: item.code, node: item, x: cx, y, w: NW, h: NH };
      nodes.push(nn);
      byCode[item.code] = nn;
      if (prev && prev.kind !== "step") edges.push({ a: prev, b: nn });
      prev = nn;
      y += NH + GY;
    }
    return { y, last: prev };
  }

  let start = null;
  let y0 = 0;
  if (opts.terminals) {
    start = { kind: "start", x: 0, y: 0, w: TS, h: TS };
    nodes.push(start);
    y0 = TS + GY;
  }
  const out = run(children, 0, y0, start);
  let endY = out.y;
  if (opts.terminals) {
    const end = { kind: "end", x: 0, y: endY, w: TS, h: TS };
    nodes.push(end);
    if (out.last) edges.push({ a: out.last, b: end });
    endY += TS + GY;
  }

  // 2026-10-13 · what a decision depends on: an edge from that step into the decision, run down
  // the right-hand side so it reads as "decided on this", not as one more step in the sequence.
  const deps = [];
  // A step's own dependencies: straight down from the step just above it, else down the right-hand
  // side like a decision's, so the edge does not run through the steps in between.
  nodes.forEach((n, k) => {
    if (n.kind !== "step") return;
    (n.node?.after_steps || []).forEach((code) => {
      const on = byCode[code];
      if (!on || on.kind !== "step") return;
      const above = nodes.slice(0, k).reverse().find((m) => m.kind === "step" && Math.abs(m.x - n.x) < 1);
      if (above === on) {
        edges.push({ a: on, b: n });
      } else {
        const e = { a: on, b: n, dep: true, after: true };
        edges.push(e);
        deps.push(e);
      }
    });
  });
  nodes.forEach((n) => {
    const on = n.kind === "dec" && n.node?.depends_on ? byCode[n.node.depends_on] : null;
    if (on) {
      const e = { a: on, b: n, dep: true, basis: n.node.basis || on.node?.step_type || null };
      edges.push(e);
      deps.push(e);
    }
  });

  const xs = nodes.flatMap((d) => [d.x - d.w / 2, d.x + d.w / 2]);
  deps.forEach((e) => xs.push(Math.max(e.a.x + e.a.w / 2, e.b.x + e.b.w / 2) + DEPX + 24));
  groups.forEach((g) => xs.push(g.x1 - 56, g.x2));   // room for the back edge outside the band
  if (!xs.length) return { nodes, edges, lanes, groups, width: 320, height: 160 };

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const dx = PADX - minX;
  nodes.forEach((n) => { n.x += dx; n.y += PADY; });
  lanes.forEach((l) => { l.x += dx; l.y += PADY; });
  groups.forEach((g) => { g.x1 += dx; g.x2 += dx; g.y1 += PADY; g.y2 += PADY; });

  return {
    nodes, edges, lanes, groups,
    width: maxX - minX + PADX * 2,
    height: endY + PADY * 2,
  };
}

/**
 * An orthogonal connector with rounded corners; straight when the two anchors line up.
 * A back edge leaves the left side and returns to the head, so it reads as the repeat it is
 * rather than as another step in the sequence.
 */
export function edgePath(a, b, back, dep) {
  if (dep) {
    // Right side, from the depended-on step down to the decision.
    const x = Math.max(a.x + a.w / 2, b.x + b.w / 2) + DEPX;
    const y1 = a.y + a.h / 2;
    const y2 = b.y + b.h / 2;
    const r = 9;
    return `M${a.x + a.w / 2} ${y1} L${x - r} ${y1} Q${x} ${y1} ${x} ${y1 + r} ` +
           `L${x} ${y2 - r} Q${x} ${y2} ${x - r} ${y2} L${b.x + b.w / 2} ${y2}`;
  }
  if (back) {
    // Outside the region, not inside it. The band is 22px clear of the nodes, so the back
    // edge runs at 34 — the repeat has to read as the repeat rather than as one more step
    // threaded through the body.
    const x = Math.min(a.x - a.w / 2, b.x - b.w / 2) - 34;
    const y1 = a.y + a.h / 2;
    const y2 = b.y + b.h / 2;
    const r = 9;
    return `M${a.x - a.w / 2} ${y1} L${x + r} ${y1} Q${x} ${y1} ${x} ${y1 - r} ` +
           `L${x} ${y2 + r} Q${x} ${y2} ${x + r} ${y2} L${b.x - b.w / 2} ${y2}`;
  }
  const x1 = a.x;
  const y1 = a.y + a.h;
  const x2 = b.x;
  const y2 = b.y;
  if (Math.abs(x1 - x2) < 1) return `M${x1} ${y1} L${x2} ${y2}`;
  const m = y1 + (y2 - y1) / 2;
  const r = 9;
  const s = x2 > x1 ? 1 : -1;
  return `M${x1} ${y1} L${x1} ${m - r} Q${x1} ${m} ${x1 + s * r} ${m} ` +
         `L${x2 - s * r} ${m} Q${x2} ${m} ${x2} ${m + r} L${x2} ${y2}`;
}

/**
 * 2026-10-22 (owner) · a step's own dependency on a step further up (step_dependency.py): from the side
 * of its icon tile, down beside the steps it skips, into the side of the dependent step's tile — attached
 * at both ends. `tile` is the icon tile's size, `top` its offset from the node's top.
 */
export function sidePath(a, b, tile, top = 2) {
  const ax = a.x + tile / 2 + top, ay = a.y + top + tile / 2;
  const bx = b.x + tile / 2 + top, by = b.y + top + tile / 2;
  const x = Math.max(a.x + a.w / 2, b.x + b.w / 2) + AFTER_X;
  const r = EDGE_R;
  return `M${ax} ${ay} L${x - r} ${ay} Q${x} ${ay} ${x} ${ay + r} ` +
         `L${x} ${by - r} Q${x} ${by} ${x - r} ${by} L${bx} ${by}`;
}
